import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, ChevronUp, X } from 'lucide-react';
import { uploadsAPI } from '../services/api';
import { useT } from '../services/i18n';

const COMPACT_LIMIT = 12;

const SubcategoryChips = ({ subcategories, labelOf = (s) => s, images = {}, counts = {}, activeSub, onSelect }) => {
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const many = subcategories.length > COMPACT_LIMIT;

  const ordered = useMemo(() => {
    const withProducts = subcategories.filter(s => counts[s]).sort((a, b) => counts[b] - counts[a]);
    const empty = subcategories.filter(s => !counts[s]);
    return [...withProducts, ...empty];
  }, [subcategories, counts]);

  const q = query.trim().toLowerCase();
  const visible = q
    ? subcategories.filter(s => s.toLowerCase().includes(q) || labelOf(s).toLowerCase().includes(q))
    : (many && !expanded ? ordered.filter((s, i) => i < COMPACT_LIMIT || s === activeSub) : ordered);
  const hidden = many && !expanded && !q ? ordered.length - visible.length : 0;

  const chip = (s) => {
    const active = activeSub === s;
    const img = images[s];
    const empty = !counts[s];
    const tone = active ? 'bg-orange-500 text-white border-orange-500' : empty ? 'bg-white text-gray-500 border-gray-200 hover:border-orange-400 hover:text-gray-800' : 'bg-gray-100 text-gray-700 border-transparent hover:border-orange-400';
    return (
      <button key={s} type="button" data-testid={`subcategory-chip-${s}`} onClick={() => onSelect(active ? '' : s)} className={`inline-flex items-center gap-1.5 text-xs rounded-full border transition-colors ${img ? 'pl-1 pr-3 py-1' : 'px-3 py-1'} ${tone}`}>
        {img && <img src={uploadsAPI.resolveUrl(img)} alt="" className="w-6 h-6 rounded-full object-cover bg-white no-placeholder" data-testid={`subcategory-image-${s}`} />}
        {labelOf(s)}{counts[s] ? <span className={active ? 'text-orange-100' : 'text-gray-400'}> ({counts[s]})</span> : null}
      </button>
    );
  };

  return (
    <div className="mt-3" data-testid="category-subcategories">
      {many && (
        <div className="relative mb-2 max-w-xs">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('cat.filterSubs', { n: subcategories.length })} className="w-full border border-gray-200 rounded-full pl-8 pr-8 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400" data-testid="subcategory-search" />
          {query && <button type="button" onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" data-testid="subcategory-search-clear"><X size={14} /></button>}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {!q && (
          <button type="button" data-testid="subcategory-chip-all" onClick={() => onSelect('')} className={`text-xs px-3 py-1 rounded-full border transition-colors ${!activeSub ? 'bg-orange-500 text-white border-orange-500' : 'bg-gray-100 text-gray-700 border-transparent hover:border-orange-400'}`}>
            {t('cat.all')}
          </button>
        )}
        {visible.map(chip)}
        {q && visible.length === 0 && <span className="text-xs text-gray-500 py-1" data-testid="subcategory-search-empty">{t('cat.noSubMatch', { q: query })}</span>}
        {hidden > 0 && (
          <button type="button" onClick={() => setExpanded(true)} className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-dashed border-orange-300 text-orange-600 hover:bg-orange-50 transition-colors" data-testid="subcategory-show-more">
            <ChevronDown size={14} /> {t('cat.moreSubs', { n: hidden })}
          </button>
        )}
        {many && expanded && !q && (
          <button type="button" onClick={() => setExpanded(false)} className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors" data-testid="subcategory-show-less">
            <ChevronUp size={14} /> {t('cat.collapse')}
          </button>
        )}
      </div>
    </div>
  );
};

export default SubcategoryChips;
